<template>
  <section class="w-full bg-stone-50 py-10 md:py-20">
    <div class="carousel-cards max-w-screen-xl mx-auto px-4 md:px-14">
      <h2
        v-if="content?.title"
        class="text-teal-900 text-center mb-6 md:mb-10 text-h4 font-semibold md:text-h2"
      >
        {{ content.title }}
      </h2>

      <ProductsShowcaseSlider
        with-arrows
        step-by-item
        :loop-copies="loopCopies"
        class="carousel-cards__rail"
        data-test-id="carousel-cards-rail"
      >
        <component
          :is="resolveLink(item?.link) ? NuxtLink : 'div'"
          v-for="item in loopedItems"
          :key="item.key"
          :to="resolveLink(item.link)"
          class="carousel-cards__item group"
          data-test-id="carousel-cards-item"
        >
          <NuxtLazyImg
            v-if="item?.image?.url"
            :src="String(item.image.url)"
            :alt="String(item.image.alternativeText || item.title || '')"
            class="carousel-cards__image w-full aspect-[5/4] object-cover rounded-lg transition-transform group-hover:scale-[1.02]"
          />
          <h3
            v-if="item?.title"
            class="text-base font-bold text-teal-900 mt-3 leading-tight"
          >
            {{ item.title }}
          </h3>
          <p
            v-if="item?.description"
            class="text-sm text-stone-700 mt-1 leading-snug"
          >
            {{ item.description }}
          </p>
        </component>
      </ProductsShowcaseSlider>
    </div>
  </section>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components'
import ProductsShowcaseSlider from '~/components/product/ProductsShowcaseSlider.vue'
import { hasLocalePrefix } from '~/utils/urlHelpers'

const { content = {} } = defineProps<{ content: any }>()

const localePath = useLocalePath()

// Order is editor-curated in Strapi, so it is rendered as-is — unlike the
// marquee layout, which shuffles.
const items = computed(() => content?.items ?? [])

/**
 * Cards per row at `md:` — see the width rule at the bottom of this file. Below
 * this many items a single copy already fits on screen, so looping would put
 * visible duplicates side by side rather than reading as an endless rail.
 */
const CARDS_PER_ROW = 4
const LOOP_COPIES = 3

const loopCopies = computed(() =>
  items.value.length > CARDS_PER_ROW ? LOOP_COPIES : 1
)

/**
 * The rail loops by rendering the cards several times over; the slider then
 * shifts scrollLeft a whole copy at a time. Keys are built here rather than in
 * the slider because the items — and therefore the keys — belong to this
 * component; repeating `<slot />` over there would duplicate them.
 */
const loopedItems = computed(() =>
  Array.from({ length: loopCopies.value }).flatMap((_, copy) =>
    items.value.map((item: any, index: number) => ({
      ...item,
      key: `${copy}-${item?.id ?? index}`,
    }))
  )
)

/**
 * Editors author these links inconsistently. Today's giftcard entries hold
 * absolute, already-locale-prefixed URLs (`https://gokonfetti.com/de-de/t/…`);
 * other Strapi blocks hold bare paths. Absolute URLs and already-prefixed paths
 * pass through untouched — NuxtLink renders the former as a plain anchor, which
 * is what the marquee layout does today — and only an unprefixed path gets
 * localised.
 */
const resolveLink = (link?: string) => {
  if (!link) return undefined
  if (/^(https?:)?\/\//.test(link) || hasLocalePrefix(link)) return link

  return localePath(link)
}
</script>

<!-- Unscoped on purpose: ProductsShowcaseSlider sets `inheritAttrs: false` and
     binds $attrs to its inner scroll container, so that element carries the
     slider's scope id rather than this component's — a scoped rule would never
     match it. ProductShowcase.vue is unscoped for the same reason. The
     `carousel-cards__` prefix is what keeps these rules contained. -->
<style lang="postcss">
/* Re-anchor the shared slider's arrows: level with the card image, sitting in the
   md:px-14 gutter beside the track, with no white fade over the cream background.
   These live on the section container, not on the rail, because the arrows are
   siblings of the rail — a custom property set on the rail would never inherit
   to them. */
.carousel-cards {
  /* cqw, not %: an arrow's `height: X%` resolves against its containing block's
     HEIGHT, but the image height is a function of the card WIDTH — the % form
     yields a 54px arrow instead of 221px. Container query units are inline-size
     relative and, unlike vw, exclude the page scrollbar, so one card's width
     (100cqw minus the four gaps, over four — matching the card width rule
     below) times the 5:4 image ratio lands exactly on the image height at
     every breakpoint. */
  container-type: inline-size;

  --slider-arrow-fade: transparent;
  --slider-arrow-inset: -3.5rem;
  --slider-arrow-height: calc((100cqw - 4rem) / 4 * 0.8);
}

.carousel-cards__rail {
  @apply flex flex-nowrap gap-4 overflow-x-auto snap-x snap-mandatory -mx-4;
  /* Without this the box reserves ~15px for a vertical scrollbar it never needs,
     which shaves the 4th card off the desktop view. */
  overflow-y: hidden;
  scrollbar-width: none;
  scroll-padding-left: 1rem;
  scroll-padding-right: 1rem;

  /* Scrollable leading/trailing inset — aligns the first card with the px-4 page
     gutter at rest, then scrolls away so cards move edge-to-edge on mobile. */
  &::before,
  &::after {
    content: '';
    flex: 0 0 1rem;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  /* Desktop scrolls via drag + arrows only, same as the event page rails. */
  @media (min-width: 768px) {
    @apply mx-0;
    overflow: hidden;
    scroll-snap-type: none;
    scroll-padding-left: 0;
    scroll-padding-right: 0;

    &::before,
    &::after {
      display: none;
    }
  }
}

.carousel-cards__item {
  flex: 0 0 auto;
  scroll-snap-align: start;
  /* ~1.5 cards visible on mobile. Widths are pure CSS so SSR emits the same
     HTML for every request (see ProductShowcase.vue). */
  width: min(280px, 62vw);

  @media (min-width: 768px) {
    /* Four gaps, not three. ProductsShowcaseSlider pages by the rail's
       offsetWidth (W), so a page of four cards must *stride* exactly W:
       4 * (card + gap) == W  =>  card == (W - 4 * gap) / 4.
       Sizing cards as (W - 3 * gap) / 4 makes four cards+gaps *fill* W but gives
       them a stride of W + gap, so every click lands one gap short — a blank
       strip on the left and a clipped 4th card, drifting a further gap per page.
       The cost is that the 4th card stops one gap shy of the right edge. */
    width: calc((100% - 4rem) / 4);
  }
}
</style>
